import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { StatCard, LoadingSpinner, StatusBadge } from '../../components/UI';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/reports'),
      api.get('/admin/applications?limit=8'),
    ]).then(([s, r, a]) => {
      setStats(s.stats);
      setReports(r);
      setRecent(a.applications || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner size="lg" className="py-20" />;

  const nocChartData = reports?.noc_wise?.map(n => ({ name: n.noc_name, count: parseInt(n.count) })) || [];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl text-white p-6" style={{ background: 'linear-gradient(135deg, #312e81 0%, #4c1d95 100%)' }}>
        <h2 className="text-xl font-bold mb-1">Admin Dashboard 🛡️</h2>
        <p className="text-purple-200 text-sm">Government NOC Management System — Full Overview</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard title="Total" value={stats?.total} icon="📋" color="blue" />
        <StatCard title="Pending" value={stats?.pending} icon="⏳" color="yellow" />
        <StatCard title="Approved" value={stats?.approved} icon="✅" color="green" />
        <StatCard title="Rejected" value={stats?.rejected} icon="❌" color="red" />
        <StatCard title="Objection" value={stats?.objection} icon="⚠️" color="orange" />
        <StatCard title="Today" value={stats?.today} icon="📅" color="purple" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">Applications by NOC Type</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={nocChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#6d28d9" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="font-semibold text-gray-800 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { to:'/admin/masters/noc', icon:'📜', label:'NOC Types', color:'bg-blue-50 border-blue-200 text-blue-700' },
              { to:'/admin/masters/employee', icon:'👷', label:'Employees', color:'bg-green-50 border-green-200 text-green-700' },
              { to:'/admin/rbac/menu-permissions', icon:'🎛️', label:'Permissions', color:'bg-purple-50 border-purple-200 text-purple-700' },
              { to:'/admin/reports', icon:'📊', label:'Reports', color:'bg-yellow-50 border-yellow-200 text-yellow-700' },
              { to:'/admin/masters/notice', icon:'📢', label:'Notices', color:'bg-red-50 border-red-200 text-red-700' },
            ].map(a => (
              <Link key={a.to} to={a.to} className={`flex items-center gap-2 p-3 border rounded-xl text-sm font-medium hover:shadow-sm transition-all ${a.color}`}>
                <span className="text-xl">{a.icon}</span><span>{a.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Recent Applications</h3>
          <Link to="/admin/applications" className="text-sm text-blue-600 hover:underline">View All →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr><th>App No</th><th>NOC Type</th><th>Applicant</th><th>Status</th><th>Dept</th><th>Date</th><th>Action</th></tr>
            </thead>
            <tbody>
              {recent.map(app => (
                <tr key={app.id}>
                  <td className="font-mono text-xs text-blue-700">{app.application_no}</td>
                  <td className="text-sm font-medium">{app.noc_name}</td>
                  <td className="text-sm">{app.user_name}</td>
                  <td><StatusBadge status={app.status} /></td>
                  <td className="text-xs text-gray-500">{app.current_dept || '—'}</td>
                  <td className="text-xs text-gray-500">{app.submitted_at ? format(new Date(app.submitted_at),'dd/MM/yyyy') : '—'}</td>
                  <td><Link to={`/dept/applications/${app.id}`} className="text-xs text-blue-600 hover:underline">View</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}